import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, Send, MessageSquare, SkipForward } from "lucide-react";
import { toast } from "sonner";

interface Draft {
  id: string;
  analysis_id: string;
  question_id: string;
  draft_answer: string;
  timestamp_ref: string | null;
  confidence: "high" | "medium" | "low" | null;
  status: "draft" | "approved" | "sent" | "skipped";
}

interface QuestionLite {
  id: string;
  user_id: string;
  question_text: string;
  status: string;
  route: string;
}

interface ProfileLite {
  id: string;
  full_name: string | null;
  email: string | null;
}

export function CallMemberAnswers({ analysisId }: { analysisId: string }) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [questions, setQuestions] = useState<Record<string, QuestionLite>>({});
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyAll, setBusyAll] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [analysisId]);

  async function load() {
    setLoading(true);
    const { data: d } = await supabase
      .from("question_answer_drafts")
      .select("*")
      .eq("analysis_id", analysisId)
      .order("created_at", { ascending: true });
    const list = (d as Draft[]) || [];
    setDrafts(list);
    const qIds = Array.from(new Set(list.map((x) => x.question_id)));
    if (qIds.length > 0) {
      const { data: qs } = await supabase
        .from("congregation_questions")
        .select("id, user_id, question_text, status, route")
        .in("id", qIds);
      const qMap: Record<string, QuestionLite> = {};
      (qs as QuestionLite[] | null)?.forEach((q) => { qMap[q.id] = q; });
      setQuestions(qMap);
      const userIds = Array.from(new Set((qs as QuestionLite[] | null || []).map((q) => q.user_id)));
      if (userIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", userIds);
        const pMap: Record<string, ProfileLite> = {};
        (profs as ProfileLite[] | null)?.forEach((p) => { pMap[p.id] = p; });
        setProfiles(pMap);
      }
    }
    setLoading(false);
  }

  const getText = (d: Draft) => edits[d.id] ?? d.draft_answer;

  async function approve(d: Draft) {
    setBusyId(d.id);
    const { error } = await supabase
      .from("question_answer_drafts")
      .update({ status: "approved", draft_answer: getText(d) })
      .eq("id", d.id);
    setBusyId(null);
    if (error) { toast.error("Could not approve."); return; }
    toast.success("Approved. Send when ready.");
    load();
  }

  async function skip(d: Draft) {
    setBusyId(d.id);
    const { error } = await supabase
      .from("question_answer_drafts")
      .update({ status: "skipped" })
      .eq("id", d.id);
    setBusyId(null);
    if (error) { toast.error("Could not skip."); return; }
    toast.success("Skipped.");
    load();
  }

  async function sendOne(d: Draft): Promise<{ ok: boolean; label: string }> {
    const q = questions[d.question_id];
    if (!q) return { ok: false, label: "question missing" };
    if (q.route === "private") return { ok: false, label: "private question, not sent" };
    if (q.status === "answered" || q.status === "closed") {
      return { ok: false, label: "question already answered" };
    }
    const answer = getText(d).trim();
    if (!answer) return { ok: false, label: "empty answer" };

    const { data: userRes } = await supabase.auth.getUser();
    const { error: uErr } = await supabase
      .from("congregation_questions")
      .update({
        status: "answered",
        answer_text: answer,
        answered_at: new Date().toISOString(),
        answered_by: userRes?.user?.id ?? null,
      })
      .eq("id", q.id);
    if (uErr) return { ok: false, label: uErr.message };

    // Notification (Ontraport tag + portal notice). Non-fatal on failure.
    try {
      await supabase.functions.invoke("notify-question-answered", {
        body: { question_id: q.id },
      });
    } catch (e) {
      console.error("notify failed for draft send", e);
    }

    await supabase
      .from("question_answer_drafts")
      .update({ status: "sent", draft_answer: answer })
      .eq("id", d.id);

    return { ok: true, label: "sent" };
  }

  async function handleSend(d: Draft) {
    setBusyId(d.id);
    const res = await sendOne(d);
    setBusyId(null);
    if (res.ok) toast.success(`Sent to ${memberName(d)}.`);
    else toast.error(`Not sent: ${res.label}`);
    load();
  }

  async function handleSendAllApproved() {
    setBusyAll(true);
    const approved = drafts.filter((d) => d.status === "approved");
    let okCount = 0;
    const failures: string[] = [];
    for (const d of approved) {
      const res = await sendOne(d);
      if (res.ok) okCount++;
      else failures.push(`${memberName(d)}: ${res.label}`);
    }
    setBusyAll(false);
    if (failures.length === 0) toast.success(`Sent ${okCount} answer${okCount === 1 ? "" : "s"}.`);
    else toast.warning(`Sent ${okCount}. ${failures.length} did not send.`, { description: failures.join(" · ") });
    load();
  }

  function memberName(d: Draft) {
    const q = questions[d.question_id];
    if (!q) return "member";
    const p = profiles[q.user_id];
    return p?.full_name || p?.email || "Member";
  }

  if (loading) return null;
  if (drafts.length === 0) return null;

  const approvedCount = drafts.filter((d) => d.status === "approved").length;

  return (
    <Card className="border-emerald-300 bg-emerald-50/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Member answers from this call
        </CardTitle>
        <CardDescription>
          Drafted from what was actually said. Nothing sends until you approve and press Send.
          Private questions are never drafted here.
        </CardDescription>
        {approvedCount > 0 && (
          <div className="pt-2">
            <Button size="sm" onClick={handleSendAllApproved} disabled={busyAll}>
              {busyAll ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Send all approved ({approvedCount})</>
              )}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {drafts.map((d) => {
          const q = questions[d.question_id];
          const isSent = d.status === "sent";
          const isSkipped = d.status === "skipped";
          const isApproved = d.status === "approved";
          const questionAlreadyAnswered = q && (q.status === "answered" || q.status === "closed");

          return (
            <div key={d.id} className={`rounded-md border bg-background p-3 space-y-2 ${isSent || isSkipped ? "opacity-70" : ""}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{memberName(d)}</span>
                {d.confidence && (
                  <Badge variant="outline" className="text-[10px] uppercase">{d.confidence} confidence</Badge>
                )}
                {d.timestamp_ref && (
                  <span className="text-xs text-muted-foreground">@ {d.timestamp_ref}</span>
                )}
                {isSent && <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Sent</Badge>}
                {isSkipped && <Badge variant="secondary">Skipped</Badge>}
                {isApproved && !isSent && <Badge className="bg-blue-500/15 text-blue-700 border-blue-300">Approved</Badge>}
                {questionAlreadyAnswered && !isSent && (
                  <Badge variant="outline">Question already answered elsewhere</Badge>
                )}
              </div>
              {q && (
                <div className="rounded-md border bg-muted/40 p-2 text-xs whitespace-pre-wrap">
                  {q.question_text}
                </div>
              )}
              <Textarea
                rows={4}
                value={getText(d)}
                onChange={(e) => setEdits((prev) => ({ ...prev, [d.id]: e.target.value }))}
                disabled={isSent || isSkipped}
              />
              <div className="flex flex-wrap gap-2 justify-end">
                {!isSent && !isSkipped && !isApproved && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => skip(d)} disabled={busyId === d.id}>
                      <SkipForward className="h-4 w-4 mr-1.5" />Skip
                    </Button>
                    <Button size="sm" onClick={() => approve(d)} disabled={busyId === d.id}>
                      {busyId === d.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Approve
                    </Button>
                  </>
                )}
                {isApproved && !isSent && (
                  <Button size="sm" onClick={() => handleSend(d)} disabled={busyId === d.id || questionAlreadyAnswered}>
                    {busyId === d.id ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" />Send</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
